import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Spacer,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useDisclosure,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { FaPencilAlt, FaPlus, FaTrash } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useRef } from 'react';
import axios from '../../../axiosConfig';
import { getAdminConsultants } from './adminConsultantsApi';

const MAX_VISIBLE_TEXT_LENGTH = 50;

const truncateValue = (value, maxLength = MAX_VISIBLE_TEXT_LENGTH) => {
  if (!value) return '—';
  return value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value;
};

const ConsultantsList = () => {
  const [consultants, setConsultants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));
  const toast = useToast();
  const cancelRef = useRef(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const fetchConsultants = async () => {
    setIsLoading(true);
    try {
      const records = await getAdminConsultants();
      setConsultants(Array.isArray(records) ? records : []);
    } catch (error) {
      console.error('Error loading consultants:', error);
      toast({
        title: 'Could not load consultants',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConsultants();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const openDeleteModal = (id) => {
    setDeleteId(id);
    onOpen();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setIsDeleting(true);
    try {
      await axios.delete(`/consultants/${deleteId}`);
      setConsultants((prev) => prev.filter((consultant) => consultant.id !== deleteId));
      toast({
        title: 'Consultant deleted',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
      setDeleteId(null);
    } catch (error) {
      console.error('Error deleting consultant:', error);
      toast({
        title: 'Could not delete consultant',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const activeBadge = (active) => (
    <Badge colorScheme={active ? 'green' : 'gray'}>{active ? 'Active' : 'Inactive'}</Badge>
  );

  return (
    <Box p={6}>
      <Flex mb={6}>
        <Heading as="h2" size="lg">
          Consultants
        </Heading>
        <Spacer />
        <Button as={Link} to="/app/admin/consultants/new" colorScheme="green" variant="outline" leftIcon={<FaPlus />}>
          New Consultant
        </Button>
      </Flex>

      {isLoading ? (
        <Spinner />
      ) : isMobile ? (
        <VStack align="stretch" spacing={4}>
          {consultants.map((consultant) => (
            <Box key={consultant.id} borderWidth="1px" borderRadius="md" p={4}>
              <VStack align="start" spacing={2}>
                <Text
                  as={Link}
                  to={`/app/admin/consultants/${consultant.id}`}
                  fontWeight="bold"
                  color="blue.600"
                  _hover={{ color: 'blue.700', textDecoration: 'underline' }}
                >
                  {consultant.name}
                </Text>
                <Text fontSize="sm">Slug: {consultant.slug || '—'}</Text>
                <Text fontSize="sm" title={consultant.email || undefined}>
                  Email: {truncateValue(consultant.email)}
                </Text>
                <Text fontSize="sm" title={consultant.booking_url || undefined}>
                  Booking URL: {truncateValue(consultant.booking_url)}
                </Text>
                {activeBadge(consultant.active)}
                <HStack pt={2}>
                  <Button
                    as={Link}
                    to={`/app/admin/consultants/${consultant.id}`}
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                  >
                    Manage
                  </Button>
                  <IconButton
                    as={Link}
                    to={`/app/admin/consultants/${consultant.id}`}
                    icon={<FaPencilAlt />}
                    aria-label="Edit Consultant"
                    title="Edit Consultant"
                    size="sm"
                    colorScheme="yellow"
                    variant="outline"
                  />
                  <IconButton
                    icon={<FaTrash />}
                    aria-label="Delete Consultant"
                    title="Delete Consultant"
                    size="sm"
                    colorScheme="red"
                    variant="outline"
                    onClick={() => openDeleteModal(consultant.id)}
                  />
                </HStack>
              </VStack>
            </Box>
          ))}
          {consultants.length === 0 && <Text>No consultants found.</Text>}
        </VStack>
      ) : (
        <Box
          w="100%"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="xl"
          bg="white"
          shadow="sm"
          overflow="hidden"
        >
          <TableContainer>
            <Table
              variant="unstyled"
              sx={{
                'th, td': { borderBottom: '1px solid', borderColor: 'gray.100' },
                'tbody tr:hover': { background: 'gray.50' },
              }}
            >
              <Thead>
                <Tr>
                  <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">Name</Th>
                  <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">Slug</Th>
                  <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">Email</Th>
                  <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">Booking URL</Th>
                  <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">Active</Th>
                  <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {consultants.length > 0 ? (
                  consultants.map((consultant) => (
                    <Tr key={consultant.id}>
                      <Td py={3} fontWeight="semibold" maxW="260px">
                        <Text
                          as={Link}
                          to={`/app/admin/consultants/${consultant.id}`}
                          color="blue.600"
                          _hover={{ color: 'blue.700', textDecoration: 'underline' }}
                        >
                          {consultant.name}
                        </Text>
                      </Td>
                      <Td py={3}>{consultant.slug || '—'}</Td>
                      <Td py={3} maxW="380px">
                        <Text title={consultant.email || undefined} whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                          {truncateValue(consultant.email)}
                        </Text>
                      </Td>
                      <Td py={3} maxW="380px">
                        <Text title={consultant.booking_url || undefined} whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                          {truncateValue(consultant.booking_url)}
                        </Text>
                      </Td>
                      <Td py={3}>{activeBadge(consultant.active)}</Td>
                      <Td py={3}>
                        <HStack spacing={2}>
                          <Tooltip label="Edit" hasArrow>
                            <IconButton
                              as={Link}
                              to={`/app/admin/consultants/${consultant.id}`}
                              icon={<FaPencilAlt />}
                              aria-label="Edit Consultant"
                              title="Edit Consultant"
                              size="sm"
                              colorScheme="yellow"
                              variant="outline"
                            />
                          </Tooltip>
                          <Tooltip label="Delete" hasArrow>
                            <IconButton
                              icon={<FaTrash />}
                              aria-label="Delete Consultant"
                              title="Delete Consultant"
                              size="sm"
                              colorScheme="red"
                              variant="outline"
                              onClick={() => openDeleteModal(consultant.id)}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={6}>No consultants found.</Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete consultant</AlertDialogHeader>
            <AlertDialogBody>Are you sure? This action cannot be undone.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" ml={3} onClick={handleDelete} isLoading={isDeleting}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default ConsultantsList;
